import { ReactTable } from "@bphxd/ds-core-react";
import { Download24, Duplicate32, Edit32, Preview24 } from "@bphxd/ds-core-react/lib/icons";
import { RowSelectionState, ColumnDef } from "@tanstack/react-table";
import {
  Dispatch,
  SetStateAction,
  useEffect,
  useMemo,
  useState,
  useCallback,
  useRef,
} from "react";
import moment from "moment";
import { Modal, Button, Input, Label } from "reactstrap";
import {
  UncontrolledDropdown,
  DropdownToggle,
  DropdownMenu,
  DropdownItem,
} from "reactstrap";
import { Down24 } from "@bphxd/ds-core-react/lib/icons";
import { downloadSingle, viewDrn } from "../../api/DRN/drndownload";
import axios from "axios";
import { updateTax } from "../../api/endpoints";
import SearchableDropdown from "./SearchableDropdown";
import { useMsal } from "@azure/msal-react";


interface TaxCertificate {
  tax_id: string;
  order_number: string;
  document_id: string;
  tax_document_id: string;
  tax_type: string;
  cust_code: string;
  cust_name: string;
  customer_email: string;
  vessel_imo_number: string;
  vessel_name: string;
  port_name: string;
  country_name: string;
  region_name: string;
  protocol_number: string | null;
  status: string;
  invalid_reason: string | null;
  effective_date: string;
  expiry_date: string;
  validator_email: string;
  uploaded_date: string;
  validity_period: string | null;
  document_link: string;
  validation_date?: string;
  isDuplicate?: boolean;
  port_id?: string;
  sold_to_id?: string;
  customer_full_name?: string;
  account_manager?: string;
  bussiness_channel?: string;
  cust_sales_area?: string;
  sales_country?: string;
  port_iso_code?: string;
  state_iso_code?: string;
  tax_name?: string;
}

export default function TaxCertificateTable({
  rowData,
  setSelectedFlatRowsVals,
  rowSelection,
  setRowSelection,
  triggerAddModal,
  onModalClosed,
  taxFilerDetails,
}: {
  rowData: TaxCertificate[];
  setSelectedFlatRowsVals: Dispatch<SetStateAction<TaxCertificate[]>>;
  rowSelection: RowSelectionState;
  setRowSelection: React.Dispatch<React.SetStateAction<RowSelectionState>>;
  triggerAddModal: boolean;
  onModalClosed: () => void;
  taxFilerDetails: any;
}) {
  const [selectedFlatRows, setSelectedFlatRows] = useState<TaxCertificate[]>([]);
  const [randomKeyTable, setRandomKeyTable] = useState<number>(0);
  const [tableData, setTableData] = useState<TaxCertificate[]>(rowData);
  const [modalOpen, setModalOpen] = useState<boolean>(false);
  const [modalData, setModalData] = useState<TaxCertificate | undefined>();
  const [modalRowIndex, setModalRowIndex] = useState<number | null>(null);
  const [uploadedFile, setUploadedFile] = useState<File | null>(null);
  const [modalErrors, setModalErrors] = useState<{ [key: string]: string }>({});
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [isCreateMode, setIsCreateMode] = useState<boolean>(false);

  // New states for dropdown search
  const [regionData, setRegionData] = useState<any[]>([]);
  const [countryOptions, setCountryOptions] = useState<any[]>([]);
  const [portOptions, setPortOptions] = useState<string[]>([]);
  const [openDropdown, setOpenDropdown] = useState<string | null>(null);
  const [businessChannelOptions, setBusinessChannelOptions] = useState<any[]>([]);
  const [custSalesAreaOptions, setcustSalesAreaOptions] = useState<any[]>([]);
  const [portIsoCodeOptions, setPortIsoCodeOptions] = useState<string[]>([]);
  const [stateIsoCodeOptions, setStateIsoCodeOptions] = useState<string[]>([]);
  const [searchText, setSearchText] = useState<{
    region: string;
    country: string;
    port: string;
    bussiness_channel: string;
    cust_sales_area: string;
    port_iso_code: string;
    state_iso_code: string;
  }>({
    region: "",
    country: "",
    port: "",
    bussiness_channel: "",
    cust_sales_area: "",
    port_iso_code: "",
    state_iso_code: ""
  });

  const userRole = sessionStorage.getItem("role");
  const isAdmin = ["admin", "tax_user"].includes(
    (userRole || "").toLowerCase()
  );
  const { instance, accounts } = useMsal();
  const accessTokenRequest = {
    account: accounts[0],
    scopes: ["User.Read"],
  };
  //console.log("accessTokenRequest", accessTokenRequest);
  const userEmail = accessTokenRequest.account.username;
  const token = sessionStorage.getItem("jwt");
  const statusOptions = ["Pending", "Valid", "Invalid"];
  const documentTypeOptions = ["TRANSACTIONAL", "PERIODICAL", "NOT APPLICABLE"];
  const validityPeriodOptions = [
    "Current Calendar Year",
    "Transaction Only",
    "1 year from document date of issuance",
    "2 years from document date of issuance",
    "3 years from document date of issuance",
    "4 years from document date of issuance",
    "5 years from document date of issuance",
    "No Expiration"
  ];
  const isSelectingRef = useRef(false);
  const PROTOCOL_REQUIRED_COUNTRIES = ["ITALY", "SLOVENIA", "CROATIA"];

  const isProtocolNumberRequired = (countryName: string): boolean => {
    return PROTOCOL_REQUIRED_COUNTRIES.includes((countryName || "").toUpperCase());
  };

  const convertFileToBase64 = (file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => {
        const result = reader.result as string;
        const base64String = result.split(",")[1];
        resolve(base64String);
      };
      reader.onerror = (error) => reject(error);
    });
  };

  // Fetch region data from API
  const fetchRegionData = useCallback(async () => {
    try {
      const { fetchRegionSearchVals } = await import("../../api/DRN/drnsearch");
      const res = await fetchRegionSearchVals(token);
      const sortedRegions = res?.region_data?.sort((a: any, b: any) =>
        a.region_name.localeCompare(b.region_name)
      );
      setRegionData(sortedRegions || []);
    } catch (error) {
      console.error("Error fetching region data:", error);
    }
  }, [token]);

  // Get countries for selected region
  const getCountriesForRegion = useCallback(
    (regionName: string) => {
      const region = regionData.find((r) => r.region_name === regionName);
      if (region?.country_name && Array.isArray(region.country_name)) {
        return region.country_name.sort((a: any, b: any) => {
          const aName = a?.name || a || "";
          const bName = b?.name || b || "";
          return aName.localeCompare(bName);
        });
      }
      return [];
    },
    [regionData]
  );

  const fetchCountryDataInitially = () => {
    const selectedRegion = modalData?.region_name || "";
    const countries = getCountriesForRegion(selectedRegion);
    setCountryOptions(countries);
  };

  const fetchPortDataInitially = () => {
    const selectedCountry = modalData?.country_name || "";
    const ports = getPortsForCountry(selectedCountry);
    setPortOptions(ports);
  };

  const getTodayDate = () => {
    const today = new Date();
    return today.toISOString().split('T')[0];
  };
  // Add this function to get port ISO code from country name
  const getPortIsoCodeFromCountry = useCallback((countryName: string): string => {
    if (!countryName) return "";

    const rawPortIsoCodeData = filterOptions['port_iso_code'] || [];

    // Find matching country in port_iso_code data
    const countryData = rawPortIsoCodeData.find((item: any) => {
      const itemName = item?.name || String(item);
      return itemName.toLowerCase() === countryName.toLowerCase();
    });

    if (countryData) {
      return countryData?.isocode || "";
    }

    return "";
  }, [regionData]);
  // Get ports for selected country
  const getPortsForCountry = useCallback((countryName: string) => {
    //console.log("Fetching ports for country:", portIsoCodeOptions, stateIsoCodeOptions);
    const countries = regionData.flatMap((r) => r.country_name || []);
    const country = countries.find((c: any) => c?.name === countryName);
    if (country?.port_name && Array.isArray(country.port_name)) {
      return country.port_name.sort();
    }
    return [];
  }, [regionData]);

  // Handle region selection
  const handleRegionChange = useCallback(
    (regionName: string) => {
      if (modalData) {
        const countries = getCountriesForRegion(regionName);
        setCountryOptions(countries);
        setPortOptions([]);
        setModalData({
          ...modalData,
          region_name: regionName,
          country_name: "",
          port_name: "",
          port_iso_code: "",
          state_iso_code: "",
        });
        setSearchText({
          region: "",
          country: "",
          port: "",
          bussiness_channel: "",
          cust_sales_area: "",
          port_iso_code: "",
          state_iso_code: ""
        });
        setModalErrors({
          ...modalErrors,
          region_name: "",
          country_name: "",
          port_name: "",
          port_iso_code: "",
          state_iso_code: "",
        });
      }
    },
    [modalData, getCountriesForRegion, modalErrors]
  );

  const handleCountryChange = useCallback(
    (countryName: string) => {
      if (modalData) {
        const portIsoCode = getPortIsoCodeFromCountry(countryName);
        const ports = getPortsForCountry(countryName);
        setPortOptions(ports);
        setModalData({
          ...modalData,
          country_name: countryName,
          port_name: "",
          port_iso_code: portIsoCode,
          state_iso_code: "",
        });
        setSearchText({
          ...searchText,
          country: "",
          port: "",
          port_iso_code: "",
          state_iso_code: "",
        });
        setModalErrors({
          ...modalErrors,
          country_name: "",
          port_name: "",
          port_iso_code: "",
          state_iso_code: "",
        });
      }
    },
    [modalData, getPortsForCountry, searchText, modalErrors, getPortIsoCodeFromCountry]
  );

  // Handle port selection
  const handlePortChange = useCallback(
    (portName: string) => {
      if (modalData) {
        setModalData({
          ...modalData,
          port_name: portName,
          port_iso_code: "",
          state_iso_code: "",
        });
        setSearchText({
          ...searchText,
          port: "",
          port_iso_code: "",
          state_iso_code: "",
        });
        setModalErrors({
          ...modalErrors,
          port_name: "",
          port_iso_code: "",
          state_iso_code: "",
        });
      }
    },
    [modalData, searchText, modalErrors]
  );

  const filterOptions = useMemo(
    () => ({
      bussiness_channel: taxFilerDetails?.bussiness_channel || [],
      cust_sales_area: taxFilerDetails?.cust_sales_area || [],
      port_iso_code: taxFilerDetails?.port_iso_code || [],
      state_iso_code: taxFilerDetails?.state_iso_code || [],
    }),
    [taxFilerDetails]
  );

  // Generic handler for all searchable dropdown fields
  const handleDropdownFieldChange = useCallback(
    (field: keyof TaxCertificate, value: string, resetFields?: (keyof TaxCertificate)[]) => {
      if (modalData) {
        const dependentReset = Object.fromEntries(
          (resetFields || []).map((f) => [f, ""])
        );
        setModalData({
          ...modalData,
          [field]: value,
          ...dependentReset,
        });
        setSearchText({
          ...searchText,
          [field]: "",
          ...dependentReset,
        });
        // Clear error for the current field and dependent fields
        const fieldsToCleanError = [field, ...(resetFields || [])];
        const updatedErrors = { ...modalErrors };
        fieldsToCleanError.forEach((f) => {
          delete updatedErrors[f];
        });
        setModalErrors(updatedErrors);
      }
    },
    [modalData, searchText, modalErrors]
  );

  // Filter options based on search text
  const getFilteredOptions = useCallback(
    (
      options: any[],
      searchValue: string,
      key: "name" | "region_name" = "name"
    ): any[] => {
      if (!searchValue) return options;
      const lowerSearch = searchValue.toLowerCase();
      return options.filter((item: any) => {
        const itemName =
          typeof item === "string" ? item : item?.[key] || String(item) || "";
        return String(itemName).toLowerCase().includes(lowerSearch);
      });
    },
    []
  );

  useEffect(() => {
    fetchRegionData();
  }, [fetchRegionData]);

  const normalizeIsoOptions = (items: any[], useIsoCode: boolean = true): string[] =>
    items.map((item) => {
      if (typeof item === 'string') return item;
      return useIsoCode ? (item?.isocode ?? String(item)) : (item?.name ?? String(item));
    });

  useEffect(() => {
    const businessChannelData = filterOptions['bussiness_channel'] || [];
    const custSalesAreaData = filterOptions['cust_sales_area'] || [];
    const portIsoCodeData = normalizeIsoOptions(filterOptions['port_iso_code'] || [], true);
    const stateIsoCodeData = normalizeIsoOptions(filterOptions['state_iso_code'] || [], false);
    setBusinessChannelOptions(businessChannelData);
    setcustSalesAreaOptions(custSalesAreaData);
    setPortIsoCodeOptions(portIsoCodeData);
    setStateIsoCodeOptions(stateIsoCodeData);
  }, [filterOptions, taxFilerDetails]);

  useEffect(() => {
    setSelectedFlatRowsVals(selectedFlatRows);
  }, [selectedFlatRows, setSelectedFlatRowsVals]);

  useEffect(() => {
    setRowSelection({});
    setRandomKeyTable(Math.floor(Math.random() * 100));
    setTableData(rowData);
  }, [rowData, setRowSelection]);

  useEffect(() => {
    if (triggerAddModal) {
      handleAddNew();
      onModalClosed();
    }
  }, [triggerAddModal, onModalClosed]);

  // when the modal is opened for duplicate/edit port and country name should be pre-populated
  useEffect(() => {
    fetchCountryDataInitially();
    fetchPortDataInitially();
  }, [modalData?.region_name, regionData]);

  function downloadPdfs(document: {
    document_type: any;
    document_id: any;
    order_number: any;
  }) {
    let docArray = [];
    let documentobj = Object();
    documentobj.document_id = document.document_id;
    documentobj.document_type = "Tax Documents";
    docArray.push(documentobj);
    console.log(document);
    downloadSingle(
      docArray,
      document.document_id,
      document.order_number,
      document.document_type,
      token
    );
  }

  const preview = async (url: string) => {
    try {
      let previewUrl: any = await viewDrn(url, token);
      window.open(previewUrl, "_blank", "width=700,height=700");
    } catch (ex) {
      console.log(ex);
    }
  };

  const getBackgroundColor = (status: string) => {
    switch (status) {
      case "Pending":
        return "#fff3cd";
      case "Valid":
        return "#d4edda";
      case "Invalid":
        return "#f8d7da";
      default:
        return "#fff3cd";
    }
  };

  const handleAddNew = () => {
    const newRecord: TaxCertificate = {
      tax_id: `NEW-${Date.now()}`,
      order_number: "",
      document_id: "",
      tax_document_id: "",
      tax_type: "",
      cust_code: "",
      cust_name: "",
      vessel_imo_number: "",
      vessel_name: "",
      port_name: "",
      country_name: "",
      region_name: "",
      protocol_number: null,
      status: "Pending",
      invalid_reason: null,
      effective_date: "",
      expiry_date: "",
      validator_email: "",
      uploaded_date: new Date().toISOString(),
      validity_period: null,
      document_link: "",
      validation_date: "",
      isDuplicate: false,
      port_id: "",
      account_manager: "",
      bussiness_channel: "",
      cust_sales_area: "",
      sales_country: "",
      customer_email: "",
      port_iso_code: "",
      state_iso_code: "",
      tax_name: "",
    };

    setModalData(newRecord);
    setModalRowIndex(-1);
    setUploadedFile(null);
    setModalErrors({});
    setIsCreateMode(true);
    setCountryOptions([]);
    setPortOptions([]);
    setSearchText({ region: "", country: "", port: "", bussiness_channel: "", cust_sales_area: "", port_iso_code: "", state_iso_code: "" });
    setModalOpen(true);
  };

  const handleDuplicate = async (row: TaxCertificate) => {
    try {
      const newTaxId = `DUP-${Date.now()}-${Math.random()
        .toString(36)
        .substr(2, 9)}`;
      const duplicatedRow: TaxCertificate = {
        tax_id: newTaxId,
        order_number: row.order_number,
        document_id: row.document_id,
        tax_document_id: row.tax_document_id,
        tax_type: row.tax_type,
        cust_code: row.cust_code,
        cust_name: row.cust_name,
        vessel_imo_number: row.vessel_imo_number,
        vessel_name: row.vessel_name,
        port_name: row.port_name,
        country_name: row.country_name,
        region_name: row.region_name,
        protocol_number: row.protocol_number,
        status: row.status,
        invalid_reason: row.invalid_reason,
        effective_date: row.effective_date,
        expiry_date: row.expiry_date,
        validator_email: row.validator_email,
        uploaded_date: row.uploaded_date,
        validity_period: row.validity_period,
        document_link: row.document_link,
        validation_date: new Date().toISOString(),
        isDuplicate: true,
        port_id: row.port_id,
        account_manager: row.account_manager,
        bussiness_channel: row.bussiness_channel,
        cust_sales_area: row.cust_sales_area,
        sales_country: row.sales_country,
        customer_email: row.customer_email,
        port_iso_code: row.port_iso_code,
        state_iso_code: row.state_iso_code,
        tax_name: row.tax_name,
      };

      setModalData(duplicatedRow);
      setModalRowIndex(-1);
      setUploadedFile(null);
      setModalErrors({});
      setIsCreateMode(false);

      if (row.region_name) {
        const countries = getCountriesForRegion(row.region_name);
        setCountryOptions(countries);

        if (row.country_name) {
          const ports = getPortsForCountry(row.country_name);
          setPortOptions(ports);
        }
      }

      setSearchText({ region: "", country: "", port: "", bussiness_channel: "", cust_sales_area: "", port_iso_code: "", state_iso_code: "" });
      setModalOpen(true);
    } catch (error) {
      console.error("Error duplicating record:", error);
    }
  };

  const handleEditRow = (row: any) => {
    console.log("Editing row:", row);
    const editData = { 
      ...row.original, 
      isDuplicate: false,
      validity_period: row.original.validity_period ? row.original.validity_period.trim() : ""
    };
    setModalData(editData);
    setModalRowIndex(row.index);
    setUploadedFile(null);
    setModalErrors({});
    setIsCreateMode(false);
    setCountryOptions([]);
    setPortOptions([]);
    setSearchText({ region: "", country: "", port: "", bussiness_channel: "", cust_sales_area: "", port_iso_code: "", state_iso_code: "" });
    setModalOpen(true);
  };
  const handleModalFieldChange = (
    fieldName: keyof TaxCertificate,
    value: any
  ) => {
    if (modalData) {
      const updatedData = {
        ...modalData,
        [fieldName]: value,
      };

      // If status is set to Invalid, clear effective_date and expiry_date
      if (fieldName === "status" && value === "Invalid") {
        updatedData.effective_date = "";
        updatedData.expiry_date = "";
      }

      // Auto-calculate expiry date when validity period changes (only if status is not Invalid)
      if (fieldName === "validity_period" && value && modalData.status !== "Invalid") {
        const effectiveDate = modalData.effective_date
          ? new Date(modalData.effective_date)
          : new Date();

        const calculatedExpiryDate = getExpirationDate(value, effectiveDate);

        if (calculatedExpiryDate) {
          updatedData.expiry_date = calculatedExpiryDate
            .toISOString()
            .split("T")[0];
        }
      }

      // Also recalculate if effective_date changes and validity period is already selected (only if status is not Invalid)
      if (fieldName === "effective_date" && modalData.validity_period && modalData.status !== "Invalid") {
        const calculatedExpiryDate = getExpirationDate(
          modalData.validity_period,
          new Date(value)
        );

        if (calculatedExpiryDate) {
          updatedData.expiry_date = calculatedExpiryDate
            .toISOString()
            .split("T")[0];
        }
      }

      setModalData(updatedData);
      setModalErrors({
        ...modalErrors,
        [fieldName]: "",
      });
    }
  };
  // const handleModalFieldChange = (
  //   fieldName: keyof TaxCertificate,
  //   value: any
  // ) => {
  //   if (modalData) {
  //     const updatedData = {
  //       ...modalData,
  //       [fieldName]: value,
  //     };

  //     // Auto-calculate expiry date when validity period changes
  //     if (fieldName === "validity_period" && value) {
  //       const effectiveDate = modalData.effective_date
  //         ? new Date(modalData.effective_date)
  //         : new Date();

  //       const calculatedExpiryDate = getExpirationDate(value, effectiveDate);

  //       if (calculatedExpiryDate) {
  //         updatedData.expiry_date = calculatedExpiryDate
  //           .toISOString()
  //           .split("T")[0];
  //       }
  //     }

  //     // Also recalculate if effective_date changes and validity period is already selected
  //     if (fieldName === "effective_date" && modalData.validity_period) {
  //       const calculatedExpiryDate = getExpirationDate(
  //         modalData.validity_period,
  //         new Date(value)
  //       );

  //       if (calculatedExpiryDate) {
  //         updatedData.expiry_date = calculatedExpiryDate
  //           .toISOString()
  //           .split("T")[0];
  //       }
  //     }

  //     setModalData(updatedData);
  //     setModalErrors({
  //       ...modalErrors,
  //       [fieldName]: "",
  //     });
  //   }
  // };
  // const handleModalFieldChange = (
  //   fieldName: keyof TaxCertificate,
  //   value: any
  // ) => {
  //   if (modalData) {
  //     setModalData({
  //       ...modalData,
  //       [fieldName]: value,
  //     });
  //     setModalErrors({
  //       ...modalErrors,
  //       [fieldName]: "",
  //     });
  //   }
  // };

  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files?.[0]) {
      const file = e.target.files[0];
      const validExtensions = ["pdf", "doc", "docx", "jpg", "jpeg", "png"];
      const fileExtension = file.name.split(".").pop()?.toLowerCase();

      if (fileExtension && validExtensions.includes(fileExtension)) {
        setUploadedFile(file);
        setModalErrors({ ...modalErrors, file: "" });
      } else {
        setModalErrors({
          ...modalErrors,
          file: "Invalid file type. Allowed: PDF, DOC, DOCX, JPG, PNG",
        });
      }
    }
  };

  const validateEditForm = (): boolean => {
    const errors: { [key: string]: string } = {};

    if (!modalData) return false;

    const requiredFields = [
      { key: "validity_period", label: "Validity Period" },
    ];

    requiredFields.forEach(({ key, label }) => {
      const value = modalData[key as keyof TaxCertificate];
      if (!value || (typeof value === "string" && value.trim() === "")) {
        errors[key] = `${label} is required`;
      }
    });

    // Protocol Number is only required for specific countries
    if (isProtocolNumberRequired(modalData.country_name)) {
      const protocolValue = modalData.protocol_number;
      if (!protocolValue || (typeof protocolValue === "string" && protocolValue.trim() === "")) {
        errors.protocol_number = "Protocol Number is required";
      }
    }

    setModalErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const validateCreateForm = (): boolean => {
    const errors: { [key: string]: string } = {};

    if (!modalData) return false;

    const requiredFields = [
      { key: "tax_name", label: "Tax Name" },
      { key: "order_number", label: "Order Number" },
      { key: "cust_code", label: "Customer Sold-To ID" },
      { key: "cust_name", label: "Customer Full Name" },
      { key: "vessel_imo_number", label: "Vessel IMO Number" },
      { key: "vessel_name", label: "Vessel Name" },
      { key: "port_name", label: "Port Name" },
      { key: "country_name", label: "Country Name" },
      { key: "region_name", label: "Region Name" },
      // { key: "account_manager", label: "Account Manager" },
      // { key: "sales_country", label: "Sales Country" },
      { key: "tax_type", label: "Document Type" },
      { key: "validity_period", label: "Validity Period" },
      { key: "effective_date", label: "Effective Date" },
      { key: "expiry_date", label: "Expiry Date" },
      { key: "tax_document_id", label: "Tax Document Id" },
      { key: "customer_email", label: "Customer Email" },

    ];

    requiredFields.forEach(({ key, label }) => {
      const value = modalData[key as keyof TaxCertificate];
      if (!value || (typeof value === "string" && value.trim() === "")) {
        errors[key] = `${label} is required`;
      }
    });

    // Protocol Number is only required for specific countries
    if (isProtocolNumberRequired(modalData.country_name)) {
      const protocolValue = modalData.protocol_number;
      if (!protocolValue || (typeof protocolValue === "string" && protocolValue.trim() === "")) {
        errors.protocol_number = "Protocol Number is required";
      }
    }

    // Numerical validation
    if (modalData.order_number && !/^\d+$/.test(modalData.order_number.trim())) {
      errors.order_number = "Order Number must contain only numbers";
    }

    if (modalData.cust_code && !/^\d+$/.test(modalData.cust_code.trim())) {
      errors.cust_code = "Customer Sold-To ID must contain only numbers";
    }

    if (modalData.vessel_imo_number && !/^\d+$/.test(modalData.vessel_imo_number.trim())) {
      errors.vessel_imo_number = "Vessel IMO Number must contain only numbers";
    }

    // Alphanumeric validation
    if (modalData.vessel_name && !/^[a-zA-Z0-9\s\-\.]+$/.test(modalData.vessel_name.trim())) {
      errors.vessel_name = "Vessel Name must contain only letters, numbers, spaces, hyphens, and periods";
    }

    // if (modalData.port_name && !/^[a-zA-Z0-9\s\-\.]+$/.test(modalData.port_name.trim())) {
    //   errors.port_name = "Port Name must contain only letters, numbers, spaces, hyphens, and periods";
    // }

    if (modalData.country_name === "UNITED STATES") {
      if (!modalData.state_iso_code || modalData.state_iso_code.trim() === "") {
        errors.state_iso_code = "State ISO Code is required when country is UNITED STATES";
      }
    }

    if (modalData.protocol_number && modalData.protocol_number.trim() !== "") {
      if (!/^[a-zA-Z0-9\s\-\.\/]+$/.test(modalData.protocol_number.trim())) {
        errors.protocol_number = "Protocol Number must contain only letters, numbers, spaces, hyphens, periods, and slashes";
      }
    }

    if (modalData.validity_period && !/^[a-zA-Z0-9\s\-]+$/.test(modalData.validity_period.trim())) {
      errors.validity_period = "Validity Period must contain only letters, numbers, spaces, and hyphens";
    }

    // Date validation
    if (modalData.effective_date) {
      const effectiveDate = new Date(modalData.effective_date);
      if (isNaN(effectiveDate.getTime())) {
        errors.effective_date = "Effective Date must be a valid date";
      }
    }

    if (modalData.expiry_date) {
      const expiryDate = new Date(modalData.expiry_date);
      if (isNaN(expiryDate.getTime())) {
        errors.expiry_date = "Expiry Date must be a valid date";
      } else if (modalData.effective_date) {
        const effectiveDate = new Date(modalData.effective_date);
        if (expiryDate < effectiveDate) {
          errors.expiry_date = "Expiry Date must be after Effective Date";
        }
      }
    }

    // Email validation


    if (modalData?.customer_email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(modalData.customer_email)) {
      errors.customer_email = "Please enter a valid email address";
    }

    // Invalid reason validation when status is Invalid
    if (modalData.status === "Invalid") {
      if (!modalData.invalid_reason || modalData.invalid_reason.trim() === "") {
        errors.invalid_reason = "Invalid Reason is required when status is Invalid";
      } else if (!/^[a-zA-Z\s\-]+$/.test(modalData.invalid_reason.trim())) {
        errors.invalid_reason = "Invalid Reason must contain only letters, spaces, and hyphens";
      }
    }

    if (!uploadedFile) {
      errors.file = "Document upload is required";
    }
    console.log("Create form validation errors:", errors);
    setModalErrors(errors);
    return Object.keys(errors).length === 0;
  };
  // Add this helper function near the top of the component (after imports)
  const getExpirationDate = (validityPeriod: string, documentDate: Date = new Date()): Date | null => {
    if (!validityPeriod) return null;

    const date = new Date(documentDate);

    switch (validityPeriod.trim().toLowerCase()) {
      case "current calendar year":
        return new Date(date.getFullYear(), 11, 31, 23, 59, 59, 999); // December 31st of the current year

      case "transaction only":
        return date;

      case "1 year from document date of issuance":
        date.setFullYear(date.getFullYear() + 1);
        return date;

      case "2 years from document date of issuance":
        date.setFullYear(date.getFullYear() + 2);
        return date;

      case "3 years from document date of issuance":
        date.setFullYear(date.getFullYear() + 3);
        return date;

      case "4 years from document date of issuance":
        date.setFullYear(date.getFullYear() + 4);
        return date;

      case "5 years from document date of issuance":
        date.setFullYear(date.getFullYear() + 5);
        return date;

      case "no expiration":
        return new Date(Date.UTC(9999, 11, 31, 23, 59, 59, 999));
      default:
        return null;
    }
  };

  const validateDuplicateForm = (): boolean => {
    const errors: { [key: string]: string } = {};
    console.log("Validating duplicate form with modalData:", modalData);

    if (!modalData) return false;

    const requiredFields = [
      { key: "tax_name", label: "Tax Name" },
      { key: "order_number", label: "Order Number" },
      { key: "cust_code", label: "Customer Sold-To-ID" },
      { key: "cust_name", label: "Customer Name" },
      { key: "vessel_imo_number", label: "Vessel IMO" },
      { key: "vessel_name", label: "Vessel Name" },
      { key: "port_name", label: "Port Name" },
      { key: "country_name", label: "Country Name" },
      { key: "region_name", label: "Region Name" },
      { key: "account_manager", label: "Account Manager" },
      { key: "sales_country", label: "Sales Country" },
      { key: "tax_type", label: "Document Type" },
      { key: "validity_period", label: "Validity Period" },
      { key: "tax_document_id", label: "Tax Document Id" },
      { key: "customer_email", label: "Customer Email" }
    ];

    requiredFields.forEach(({ key, label }) => {
      const value = modalData[key as keyof TaxCertificate];
      if (!value || (typeof value === "string" && value.trim() === "")) {
        errors[key] = `${label} is required`;
      }
    });

    // Protocol Number is only required for specific countries
    if (isProtocolNumberRequired(modalData.country_name)) {
      const protocolValue = modalData.protocol_number;
      if (!protocolValue || (typeof protocolValue === "string" && protocolValue.trim() === "")) {
        errors.protocol_number = "Protocol Number is required";
      }
    }

    // Validate invalid_reason when status is Invalid
    if (modalData.status === "Invalid") {
      if (!modalData.invalid_reason || modalData.invalid_reason.trim() === "") {
        errors.invalid_reason = "Invalid Reason is required when status is Invalid";
      }
    }

    // Email validation
    if (
      modalData?.customer_email &&
      !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(modalData.customer_email)
    ) {
      errors.customer_email = "Please enter a valid email address";
    }

    console.log("Duplicate form validation errors:", errors);
    setModalErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const closeModal = () => {
    setModalOpen(false);
    setModalData(undefined);
    setModalRowIndex(null);
    setUploadedFile(null);
    setModalErrors({});
    setIsSubmitting(false);
    setIsCreateMode(false);
    setCountryOptions([]);
    setPortOptions([]);
    setSearchText({ region: "", country: "", port: "", bussiness_channel: "", cust_sales_area: "", port_iso_code: "", state_iso_code: "" });
    setOpenDropdown(null);
  };

  const updateRow = async () => {
    if (!modalData) return;
    const updatedEffectiveDate = new Date().toISOString();
    const validation_email = userEmail;
    const validator_display_name = userEmail?.split('@')[0] || userEmail;

    if (modalData.status === "Invalid") {
      if (!modalData.invalid_reason || modalData.invalid_reason.trim() === "") {
        setModalErrors({
          ...modalErrors,
          invalid_reason: "Invalid Reason is required when status is Invalid",
        });
        return;
      }
    }

    if (isCreateMode) {
      if (!validateCreateForm()) {
        return;
      }
    } else {
      if (modalData.isDuplicate) {
        if (!validateDuplicateForm()) {
          return;
        }
      } else {
        if (!validateEditForm()) {
          return;
        }
      }
    }

    setIsSubmitting(true);

    try {
      // Find the ISO code for the selected state
      const rawStateData = filterOptions['state_iso_code'] || [];
      const selectedStateIsoCode = (() => {
        // Check if modalData.state_iso_code is already an ISO code
        const isAlreadyIsoCode = rawStateData.some(
          (state: any) => (state?.isocode ?? String(state)) === modalData.state_iso_code
        );

        if (isAlreadyIsoCode) {
          return modalData.state_iso_code;
        }

        // Otherwise, find the ISO code by name
        const stateObj = rawStateData.find(
          (state: any) => state?.name === modalData.state_iso_code
        );
        return stateObj?.isocode ?? modalData.state_iso_code;
      })();

      const certificateData = {
        tax_id: modalData.tax_id,
        tax_name: modalData.tax_name,
        order_number: modalData.order_number,
        document_id: modalData.document_id,
        tax_document_id: modalData.tax_document_id,
        customer_email: modalData.customer_email,
        tax_type: modalData.tax_type,
        cust_code: modalData.cust_code,
        cust_name: modalData.cust_name,
        vessel_imo_number: modalData.vessel_imo_number,
        vessel_name: modalData.vessel_name,
        port_name: modalData.port_name,
        country_name: modalData.country_name,
        region_name: modalData.region_name,
        protocol_number: modalData.protocol_number || "",
        status: modalData.status,
        invalid_reason: modalData.status === 'Valid' ? "" : modalData.invalid_reason || "",
        effective_date: modalData.status === 'Invalid' ? "" : modalData.effective_date,
        expiry_date: modalData.status === 'Invalid' ? "" : modalData.expiry_date,
        validator_email: validation_email || "",
        uploaded_date: modalData.uploaded_date,
        validity_period: modalData.validity_period || "",
        document_link: modalData.document_link,
        validation_date: updatedEffectiveDate || "",
        port_id: modalData.isDuplicate ? null : (modalData.port_id || ""),
        account_manager: modalData.account_manager || "",
        bussiness_channel: modalData.bussiness_channel || "",
        cust_sales_area: modalData.cust_sales_area || "",
        sales_country: modalData.sales_country || "",
        port_iso_code: modalData.port_iso_code || getPortIsoCodeFromCountry(modalData.country_name) || "",
        state_iso_code: selectedStateIsoCode || "",
        isDuplicate: modalData.isDuplicate || isCreateMode,
      };

      const isCreate = modalRowIndex === -1;

      const formData = new FormData();
      Object.entries(certificateData).forEach(([key, value]) => {
        formData.append(key, String(value ?? ""));
      });

      if (uploadedFile) {
        const base64String = await convertFileToBase64(uploadedFile);
        formData.append("document_file_base64", base64String);
        formData.append("document_file_name", uploadedFile.name);
        formData.append("document_file_type", uploadedFile.type);
      }

      console.log("========== TAX CERTIFICATE DATA LOG ==========");
      console.log(
        "Mode:",
        isCreate
          ? "CREATE"
          : certificateData.isDuplicate
            ? "DUPLICATE"
            : "EDIT"
      );
      console.log("Certificate Data:", certificateData);
      const response = await axios.post(updateTax, formData, {
        headers: {
          "Content-Type": "application/json",
          Authorizationtoken: `Bearer ${token}`,
        },
      });

      console.log("Backend Response:", response.data);
      const updatedData = [...tableData];

      if (isCreate) {
        updatedData.push({ ...modalData, isDuplicate: false });
      } else if (modalRowIndex !== null && modalRowIndex >= 0) {
        updatedData[modalRowIndex] = {
          ...modalData,
          isDuplicate: false,
          validation_date: updatedEffectiveDate,
          validator_email: validator_display_name,
          effective_date: modalData.status === 'Invalid' ? "" : modalData.effective_date,
          expiry_date: modalData.status === 'Invalid' ? "" : modalData.expiry_date,
          invalid_reason: modalData.status === 'Valid' ? "" : modalData.invalid_reason
        };
      }

      setTableData(updatedData);
      closeModal();
    } catch (error) {
      console.error("Error saving row:", error);
      const errorMessage = axios.isAxiosError(error)
        ? error.response?.data?.message || error.message
        : "Please try again.";
      setModalErrors({
        ...modalErrors,
        submit: `Failed to save record. ${errorMessage}`,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const renderStatusDisplay = (props: any) => {
    const currentValue = props.getValue();
    return (
      <span
        style={{
          padding: "4px 8px",
          borderRadius: "4px",
          backgroundColor: getBackgroundColor(currentValue),
          display: "inline-block",
        }}
      >
        {currentValue}
      </span>
    );
  };

  const renderDateDisplay = (value: any) => {
    return value ? moment(value).format("DD MMM YYYY") : "-";
  };

  // Render Region Dropdown
  const renderRegionDropdown = useCallback(() => {
    const filteredRegions = getFilteredOptions(
      regionData,
      searchText.region,
      "region_name"
    );
    const selectedValue = modalData?.region_name || "";

    return (
      <div>
        <Label className="fw-bold mb-2">
          Region
          {isCreateMode && <span style={{ color: "red", marginLeft: "4px" }}>*</span>}
          {!isCreateMode && modalData?.isDuplicate && (
            <span style={{ color: "red", marginLeft: "4px" }}>*</span>
          )}
        </Label>
        <UncontrolledDropdown
          className="w-100"
          isOpen={openDropdown === "region"}
          toggle={() =>
            setOpenDropdown(openDropdown === "region" ? null : "region")
          }
        >
          <DropdownToggle
            className="w-100 rounded-0 border d-flex justify-content-between align-items-center"
            color="#fff"
            caret={false}
            disabled={!isCreateMode && !modalData?.isDuplicate}
            style={{
              padding: "6px 8px",
              backgroundColor:
                !isCreateMode && !modalData?.isDuplicate
                  ? "#e9ecef"
                  : "#fff",
            }}
          >
            <span className="text-truncate">{selectedValue || "Select Region"}</span>
            <Down24
              color={
                !isCreateMode && !modalData?.isDuplicate ? "#ccc" : "#007f00"
              }
            />
          </DropdownToggle>

          <DropdownMenu className="w-100">
            <div className="p-2">
              <Input
                type="text"
                placeholder="Type to search..."
                value={searchText.region}
                onChange={(e) =>
                  setSearchText({ ...searchText, region: e.target.value })
                }
                onKeyDown={(e) => e.stopPropagation()}
                className="form-control-sm"
              />
            </div>

            {filteredRegions.length > 0 ? (
              <div style={{ maxHeight: "250px", overflowY: "auto" }}>
                {filteredRegions.map((region: any, index: number) => (
                  <DropdownItem
                    key={`region_${index}`}
                    onClick={() => {
                      handleRegionChange(region.region_name);
                      setOpenDropdown(null);
                    }}
                  >
                    {region.region_name}
                  </DropdownItem>
                ))}
              </div>
            ) : (
              <DropdownItem disabled className="text-center p-2">
                No results found
              </DropdownItem>
            )}
          </DropdownMenu>
        </UncontrolledDropdown>
        {modalErrors.region_name && (
          <small className="text-danger d-block mt-1">
            {modalErrors.region_name}
          </small>
        )}
      </div>
    );
  }, [
    regionData,
    searchText.region,
    modalData?.region_name,
    modalData?.isDuplicate,
    isCreateMode,
    openDropdown,
    modalErrors.region_name,
    getFilteredOptions,
    handleRegionChange,
  ]);

  // Render Country Dropdown
  const renderCountryDropdown = useCallback(() => {
    const filteredCountries = getFilteredOptions(
      countryOptions,
      searchText.country,
      "name"
    );
    const selectedValue = modalData?.country_name || "";
    const isDisabled =
      !modalData?.region_name ||
      (!isCreateMode && !modalData?.isDuplicate);

    return (
      <div>
        <Label className="fw-bold mb-2">
          Country
          {(isCreateMode || modalData?.isDuplicate) && (
            <span style={{ color: "red", marginLeft: "4px" }}>*</span>
          )}
        </Label>
        <UncontrolledDropdown
          className="w-100"
          isOpen={openDropdown === "country" && !isDisabled}
          toggle={() =>
            !isDisabled &&
            setOpenDropdown(openDropdown === "country" ? null : "country")
          }
        >
          <DropdownToggle
            className="w-100 rounded-0 border d-flex justify-content-between align-items-center"
            color="#fff"
            caret={false}
            disabled={isDisabled}
            style={{
              padding: "6px 8px",
              backgroundColor: isDisabled ? "#e9ecef" : "#fff",
            }}
          >
            <span className="text-truncate">{selectedValue || "Select Country"}</span>
            <Down24 color={isDisabled ? "#ccc" : "#007f00"} />
          </DropdownToggle>

          <DropdownMenu className="w-100">
            <div className="p-2">
              <Input
                type="text"
                placeholder="Type to search..."
                value={searchText.country}
                onChange={(e) =>
                  setSearchText({ ...searchText, country: e.target.value })
                }
                onKeyDown={(e) => e.stopPropagation()}
                className="form-control-sm"
              />
            </div>

            {filteredCountries.length > 0 ? (
              <div style={{ maxHeight: "250px", overflowY: "auto" }}>
                {filteredCountries.map((country: any, index: number) => {
                  const countryName = country?.name || String(country);
                  return (
                    <DropdownItem
                      key={`country_${index}`}
                      onClick={() => {
                        handleCountryChange(countryName);
                        setOpenDropdown(null);
                      }}
                    >
                      {countryName}
                    </DropdownItem>
                  );
                })}
              </div>
            ) : (
              <DropdownItem disabled className="text-center p-2">
                No results found
              </DropdownItem>
            )}
          </DropdownMenu>
        </UncontrolledDropdown>
        {modalErrors.country_name && (
          <small className="text-danger d-block mt-1">
            {modalErrors.country_name}
          </small>
        )}
      </div>
    );
  }, [
    countryOptions,
    searchText.country,
    modalData?.country_name,
    modalData?.region_name,
    modalData?.isDuplicate,
    isCreateMode,
    openDropdown,
    modalErrors.country_name,
    getFilteredOptions,
    handleCountryChange,
  ]);

  // Render Port Dropdown
  const renderPortDropdown = useCallback(() => {
    const filteredPorts = getFilteredOptions(
      portOptions,
      searchText.port
    );
    const selectedValue = modalData?.port_name || "";
    const isDisabled =
      !modalData?.country_name ||
      (!isCreateMode && !modalData?.isDuplicate);

    return (
      <div>
        <Label className="fw-bold mb-2">
          Port
          {(isCreateMode || modalData?.isDuplicate) && (
            <span style={{ color: "red", marginLeft: "4px" }}>*</span>
          )}
        </Label>
        <UncontrolledDropdown
          className="w-100"
          isOpen={openDropdown === "port" && !isDisabled}
          toggle={() =>
            !isDisabled &&
            setOpenDropdown(openDropdown === "port" ? null : "port")
          }
        >
          <DropdownToggle
            className="w-100 rounded-0 border d-flex justify-content-between align-items-center"
            color="#fff"
            caret={false}
            disabled={isDisabled}
            style={{
              padding: "6px 8px",
              backgroundColor: isDisabled ? "#e9ecef" : "#fff",
            }}
          >
            <span className="text-truncate">{selectedValue || "Select Port"}</span>
            <Down24 color={isDisabled ? "#ccc" : "#007f00"} />
          </DropdownToggle>

          <DropdownMenu className="w-100">
            <div className="p-2">
              <Input
                type="text"
                placeholder="Type to search..."
                value={searchText.port}
                onChange={(e) =>
                  setSearchText({ ...searchText, port: e.target.value })
                }
                onKeyDown={(e) => e.stopPropagation()}
                className="form-control-sm"
              />
            </div>

            {filteredPorts.length > 0 ? (
              <div style={{ maxHeight: "250px", overflowY: "auto" }}>
                {filteredPorts.map((port: string, index: number) => (
                  <DropdownItem
                    key={`port_${index}`}
                    onClick={() => {
                      handlePortChange(port);
                      setOpenDropdown(null);
                    }}
                  >
                    {port}
                  </DropdownItem>
                ))}
              </div>
            ) : (
              <DropdownItem disabled className="text-center p-2">
                No results found
              </DropdownItem>
            )}
          </DropdownMenu>
        </UncontrolledDropdown>
        {modalErrors.port_name && (
          <small className="text-danger d-block mt-1">
            {modalErrors.port_name}
          </small>
        )}
      </div>
    );
  }, [
    portOptions,
    searchText.port,
    modalData?.port_name,
    modalData?.country_name,
    modalData?.isDuplicate,
    isCreateMode,
    openDropdown,
    modalErrors.port_name,
    getFilteredOptions,
    handlePortChange,
  ]);

  const handleSearchChange = (
    field: string,
    value: string
  ) => {
    setSearchText((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleDropdownToggle = (field: string) => {
    if (isSelectingRef.current) {
      isSelectingRef.current = false;
      return;
    }
    setOpenDropdown(prev => {
      console.log("prev:", prev);
      return prev === field ? null : field;
    });
  };

  const cols: {
    accessorKey?: string;
    header: any;
    cell?: any;
    sortingFn?: any;
    id?: any;
  }[] = [
      ...(isAdmin
        ? [
          {
            id: "actions",
            header: () => {
              return <div className="alignVDheader">Actions</div>;
            },
            cell: ({ row }: any) => {
              return (
                <div className="ms-3 doctypeCell">
                  <div>
                    <Edit32
                      style={{
                        color: "black",
                        cursor: "pointer",
                        width: "18px",
                        height: "18px",
                      }}
                      className="action-btn"
                      onClick={() => handleEditRow(row)}
                      title="Edit"
                    />
                  </div>
                  <div>
                    <Duplicate32
                      style={{
                        color: "black",
                        cursor: "pointer",
                        width: "18px",
                        height: "18px",
                      }}
                      className="action-btn"
                      onClick={() =>
                        handleDuplicate(
                          (row as { original: TaxCertificate }).original
                        )
                      }
                      title="Duplicate"
                    />
                  </div>
                </div>
              );
            },
          },
        ]
        : []),
      {
        id: "view&download",
        header: () => {
          return <div className="alignVDheader">View / Download</div>;
        },
        cell: ({ row }: any) => {
          return (
            <div className="ms-3 doctypeCell">
              <div>
                {row.original.document_link !== null && (
                  <Preview24
                    style={{ color: "black", cursor: "pointer" }}
                    className="dwn-view-btn"
                    onClick={() => {
                      preview(row.original.document_link);
                    }}
                    id={"tooltip_row" + row.id}
                  />
                )}
              </div>
              <div>
                {row.original.document_link !== null && (
                  <Download24
                    style={{ color: "black", cursor: "pointer" }}
                    className="dwn-view-btn"
                    onClick={() => {
                      downloadPdfs(row.original);
                    }}
                  />
                )}
              </div>
            </div>
          );
        },
      },
      {
        accessorKey: "document_id",
        header: "Document ID",
        sortingFn: "alphanumeric",
      },
      {
        accessorKey: "order_number",
        header: "Order Number",
        sortingFn: "alphanumeric",
        cell: (props: any) => {
          return <div>{props.getValue()}</div>;
        },
      },
      {
        accessorKey: "cust_code",
        header: "Customer Sold-To-ID",
        cell: (props: any) => {
          return <div>{props.getValue()}</div>;
        },
      },
      {
        accessorKey: "cust_name",
        header: "Customer Name",
        cell: (props: any) => {
          return <div>{props.getValue()}</div>;
        },
      },
      {
        accessorKey: "vessel_imo_number",
        header: "Vessel IMO",
        cell: (props: any) => {
          return <div>{props.getValue()}</div>;
        },
      },
      {
        accessorKey: "vessel_name",
        header: "Vessel",
        cell: (props: any) => {
          return <div>{props.getValue()}</div>;
        },
      },
      {
        accessorKey: "country_name",
        header: "Country",
        cell: (props: any) => {
          return <div>{props.getValue()}</div>;
        },
      },
      {
        accessorKey: "port_name",
        header: "Port",
        cell: (props: any) => {
          return <div>{props.getValue()}</div>;
        },
      },
      {
        accessorKey: "state_iso_code",
        header: "State(US)",
        cell: (props: any) => {
          const selectedValue = props.getValue();
          const rawStateData = filterOptions['state_iso_code'] || [];

          // First, try to find by isocode
          let stateObj = rawStateData.find(
            (state: any) => (state?.isocode ?? String(state)) === selectedValue
          );

          // If not found by isocode, try to find by name
          if (!stateObj) {
            stateObj = rawStateData.find(
              (state: any) => state?.name === selectedValue
            );
          }

          // Display the name if found, otherwise the raw value
          const stateName = stateObj?.name || selectedValue;
          return <div>{stateName || "-"}</div>;
        },
      },
      {
        accessorKey: "tax_name",
        header: "Tax Type",
        cell: (props: any) => {
          return <div>{props.getValue()}</div>;
        },
      },
      {
        accessorKey: "uploaded_date",
        header: "Document Recive date",
        cell: (props: any) => {
          return (
            <div className="invDateCell">
              {props.getValue() !== null
                ? moment(props.getValue()).format("DD MMM YYYY")
                : ""}
            </div>
          );
        },
      },
      {
        accessorKey: "protocol_number",
        header: "Protocol Number",
        cell: (props: any) => {
          return <div>{props.getValue() || "-"}</div>;
        },
      },
      {
        accessorKey: "status",
        header: "Status",
        cell: renderStatusDisplay,
      },
      {
        accessorKey: "invalid_reason",
        header: "Invalid Reason",
        cell: (props: any) => {
          return <div>{props.getValue() || "-"}</div>;
        },
      },
      {
        accessorKey: "validity_period",
        header: "Validity Period",
        cell: (props: any) => {
          return <div>{props.getValue() || "-"}</div>;
        },
      },
      {
        accessorKey: "effective_date",
        header: "Effective Date",
        cell: (props: any) => {
          return <div>{renderDateDisplay(props.getValue())}</div>;
        },
      },
      {
        accessorKey: "expiry_date",
        header: "Expiration Date",
        cell: (props: any) => {
          return <div>{renderDateDisplay(props.getValue())}</div>;
        },
      },
      {
        accessorKey: "validation_date",
        header: "Validation Date",
        cell: (props: any) => {
          return <div>{renderDateDisplay(props.getValue())}</div>;
        },
      },
      {
        accessorKey: "validator_email",
        header: "Validator Name",
        cell: (props: any) => {
          return <div>{props.getValue()}</div>;
        },
      },
    ];

  const columns = useMemo<ColumnDef<TaxCertificate>[]>(() => cols, [isAdmin, filterOptions]);

  return (
    <>
      <ReactTable<TaxCertificate>
        tableClassName="table-main-class"
        columns={columns}
        data={tableData}
        striped={true}
        responsive={true}
        rowSelection={rowSelection}
        setRowSelection={setRowSelection}
        setSelectedFlatRows={setSelectedFlatRows}
        key={randomKeyTable}
      />

      {/* Create New Record Modal */}
      <Modal
        isOpen={modalOpen && isCreateMode}
        toggle={closeModal}
        centered
        size="lg"
      >
        <div className="modal-header border-bottom">
          <h5 className="mb-0">Create Tax Certificate</h5>
          <button
            type="button"
            className="btn-close"
            onClick={closeModal}
            disabled={isSubmitting}
          ></button>
        </div>
        <div
          className="modal-body bg-light"
          style={{ maxHeight: "70vh", overflowY: "auto", padding: "20px" }}
        >
          {modalData && (
            <div>
              {modalErrors.submit && (
                <div
                  className="alert alert-danger alert-dismissible fade show"
                  role="alert"
                >
                  {modalErrors.submit}
                  <button
                    type="button"
                    className="btn-close"
                    onClick={() =>
                      setModalErrors({ ...modalErrors, submit: "" })
                    }
                    disabled={isSubmitting}
                  />
                </div>
              )}

              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "1fr 1fr",
                  gap: "16px",
                }}
              >
                {/* Order Number */}
                <div>
                  <Label className="fw-bold mb-2">
                    Order Number
                    <span style={{ color: "red", marginLeft: "4px" }}>*</span>
                  </Label>
                  <Input
                    bsSize="sm"
                    type="text"
                    value={modalData.order_number}
                    onChange={(e) =>
                      handleModalFieldChange("order_number", e.target.value)
                    }
                    placeholder="Enter order number"
                    disabled={isSubmitting}
                    style={{ backgroundColor: "#fff" }}
                    className={modalErrors.order_number ? "is-invalid" : ""}
                  />
                  {modalErrors.order_number && (
                    <small className="text-danger d-block mt-1">
                      {modalErrors.order_number}
                    </small>
                  )}
                </div>

                {/* Sold To ID */}
                <div>
                  <Label className="fw-bold mb-2">
                    Customer Sold-To ID
                    <span style={{ color: "red", marginLeft: "4px" }}>*</span>
                  </Label>
                  <Input
                    bsSize="sm"
                    type="text"
                    value={modalData.cust_code}
                    onChange={(e) =>
                      handleModalFieldChange("cust_code", e.target.value)
                    }
                    placeholder="Enter sold-to ID"
                    disabled={isSubmitting}
                    style={{ backgroundColor: "#fff" }}
                    className={modalErrors.cust_code ? "is-invalid" : ""}
                  />
                  {modalErrors.cust_code && (
                    <small className="text-danger d-block mt-1">
                      {modalErrors.cust_code}
                    </small>
                  )}
                </div>

                {/* Customer Full Name */}
                <div>
                  <Label className="fw-bold mb-2">
                    Customer Full Name
                    <span style={{ color: "red", marginLeft: "4px" }}>*</span>
                  </Label>
                  <Input
                    bsSize="sm"
                    type="text"
                    value={modalData.cust_name}
                    onChange={(e) =>
                      handleModalFieldChange("cust_name", e.target.value)
                    }
                    placeholder="Enter customer name"
                    disabled={isSubmitting}
                    style={{ backgroundColor: "#fff" }}
                    className={modalErrors.cust_name ? "is-invalid" : ""}
                  />
                  {modalErrors.cust_name && (
                    <small className="text-danger d-block mt-1">
                      {modalErrors.cust_name}
                    </small>
                  )}
                </div>

                {/* Vessel IMO Number */}
                <div>
                  <Label className="fw-bold mb-2">
                    Vessel IMO Number
                    <span style={{ color: "red", marginLeft: "4px" }}>*</span>
                  </Label>
                  <Input
                    bsSize="sm"
                    type="text"
                    value={modalData.vessel_imo_number}
                    onChange={(e) =>
                      handleModalFieldChange("vessel_imo_number", e.target.value)
                    }
                    placeholder="Enter vessel IMO"
                    disabled={isSubmitting}
                    style={{ backgroundColor: "#fff" }}
                    className={
                      modalErrors.vessel_imo_number ? "is-invalid" : ""
                    }
                  />
                  {modalErrors.vessel_imo_number && (
                    <small className="text-danger d-block mt-1">
                      {modalErrors.vessel_imo_number}
                    </small>
                  )}
                </div>

                {/* Vessel Name */}
                <div>
                  <Label className="fw-bold mb-2">
                    Vessel Name
                    <span style={{ color: "red", marginLeft: "4px" }}>*</span>
                  </Label>
                  <Input
                    bsSize="sm"
                    type="text"
                    value={modalData.vessel_name}
                    onChange={(e) =>
                      handleModalFieldChange("vessel_name", e.target.value)
                    }
                    placeholder="Enter vessel name"
                    disabled={isSubmitting}
                    style={{ backgroundColor: "#fff" }}
                    className={modalErrors.vessel_name ? "is-invalid" : ""}
                  />
                  {modalErrors.vessel_name && (
                    <small className="text-danger d-block mt-1">
                      {modalErrors.vessel_name}
                    </small>
                  )}
                </div>

                {/* Region Dropdown */}
                {renderRegionDropdown()}

                {/* Country Dropdown */}
                {renderCountryDropdown()}

                {/* Port Dropdown */}
                {renderPortDropdown()}

                {/* State ISO Code - only shown when country is UNITED STATES */}
                {modalData?.country_name === 'UNITED STATES' && (
                  <div>
                    <SearchableDropdown
                      label="State ISO Code"
                      fieldKey="state_iso_code"
                      options={stateIsoCodeOptions}
                      selectedValue={
                        modalData?.state_iso_code
                          ? (() => {
                            const rawStateData = filterOptions['state_iso_code'] || [];
                            const stateObj = rawStateData.find(
                              (state: any) => (state?.isocode ?? String(state)) === modalData.state_iso_code
                            );
                            return stateObj?.name || modalData.state_iso_code;
                          })()
                          : ""
                      }
                      searchValue={searchText.state_iso_code}
                      placeholder="Select State ISO Code"
                      isRequired={false}
                      openDropdown={openDropdown}
                      disabled={modalData?.isDuplicate}
                      onToggle={handleDropdownToggle}
                      onSearchChange={handleSearchChange}
                      onSelect={(value) => {
                        isSelectingRef.current = true;
                        handleDropdownFieldChange('state_iso_code', value);
                        setOpenDropdown(null);
                      }}
                    />
                    {modalErrors.state_iso_code && (
                      <small className="text-danger d-block mt-1">
                        {modalErrors.state_iso_code}
                      </small>
                    )}
                  </div>
                )}

                {/* Account Manager */}
                {/* <div>
                  <Label className="fw-bold mb-2">
                    Account Manager
                    <span style={{ color: "red", marginLeft: "4px" }}>*</span>
                  </Label>
                  <Input
                    bsSize="sm"
                    type="text"
                    value={modalData.account_manager || ""}
                    onChange={(e) =>
                      handleModalFieldChange("account_manager", e.target.value)
                    }
                    placeholder="Enter account manager"
                    disabled={isSubmitting}
                    style={{ backgroundColor: "#fff" }}
                    className={
                      modalErrors.account_manager ? "is-invalid" : ""
                    }
                  />
                  {modalErrors.account_manager && (
                    <small className="text-danger d-block mt-1">
                      {modalErrors.account_manager}
                    </small>
                  )}
                </div> */}

                {/* Sales Country */}
                {/* <div>
                  <Label className="fw-bold mb-2">
                    Sales Country
                    <span style={{ color: "red", marginLeft: "4px" }}>*</span>
                  </Label>
                  <Input
                    bsSize="sm"
                    type="text"
                    value={modalData.sales_country || ""}
                    onChange={(e) =>
                      handleModalFieldChange("sales_country", e.target.value)
                    }
                    placeholder="Enter sales country"
                    disabled={isSubmitting}
                    style={{ backgroundColor: "#fff" }}
                    className={modalErrors.sales_country ? "is-invalid" : ""}
                  />
                  {modalErrors.sales_country && (
                    <small className="text-danger d-block mt-1">
                      {modalErrors.sales_country}
                    </small>
                  )}
                </div> */}

                {/* Document Type */}
                <div>
                  <Label className="fw-bold mb-2">
                    Document Type
                    <span style={{ color: "red", marginLeft: "4px" }}>*</span>
                  </Label>
                  <select
                    className={`form-select form-select-sm ${modalErrors.tax_type ? "is-invalid" : ""
                      }`}
                    value={modalData.tax_type}
                    onChange={(e) =>
                      handleModalFieldChange("tax_type", e.target.value)
                    }
                    style={{ padding: "6px 8px", backgroundColor: "#fff" }}
                    disabled={isSubmitting}
                  >
                    <option value="">Select Document Type</option>
                    {documentTypeOptions.map((option) => (
                      <option key={option} value={option}>
                        {option}
                      </option>
                    ))}
                  </select>
                  {modalErrors.tax_type && (
                    <small className="text-danger d-block mt-1">
                      {modalErrors.tax_type}
                    </small>
                  )}
                </div>

                {/* Tax Document ID */}
                <div>
                  <Label className="fw-bold mb-2">
                    Tax Document ID
                    <span style={{ color: "red", marginLeft: "4px" }}>*</span>
                  </Label>
                  <Input
                    className={
                      modalErrors.tax_document_id ? "is-invalid" : ""
                    }
                    placeholder="Enter Tax Document Id"
                    bsSize="sm"
                    type="text"
                    value={modalData.tax_document_id || ''}
                    onChange={(e) =>
                      handleModalFieldChange("tax_document_id", e.target.value)
                    }
                    disabled={isSubmitting}
                    style={{ backgroundColor: "#fff" }}
                  />
                  {modalErrors.tax_document_id && (
                    <small className="text-danger d-block mt-1">
                      {modalErrors.tax_document_id}
                    </small>
                  )}
                </div>

                {/* Tax Name */}
                <div>
                  <Label className="fw-bold mb-2">
                    Tax Name
                    <span style={{ color: "red", marginLeft: "4px" }}>*</span>
                  </Label>
                  <Input
                    bsSize="sm"
                    type="text"
                    value={modalData.tax_name || ""}
                    onChange={(e) =>
                      handleModalFieldChange("tax_name", e.target.value)
                    }
                    placeholder="Enter tax name"
                    disabled={isSubmitting}
                    style={{ backgroundColor: "#fff" }}
                    className={modalErrors.tax_name ? "is-invalid" : ""}
                  />
                  {modalErrors.tax_name && (
                    <small className="text-danger d-block mt-1">
                      {modalErrors.tax_name}
                    </small>
                  )}
                </div>

                {/* Customer Email */}
                <div>
                  <Label className="fw-bold mb-2">
                    Customer Email
                    <span style={{ color: "red", marginLeft: "4px" }}>*</span>
                  </Label>
                  <Input
                    bsSize="sm"
                    type="text"
                    value={modalData.customer_email || ""}
                    onChange={(e) =>
                      handleModalFieldChange("customer_email", e.target.value)
                    }
                    placeholder="Enter customer email"
                    disabled={isSubmitting}
                    style={{ backgroundColor: "#fff" }}
                    className={
                      modalErrors.customer_email ? "is-invalid" : ""
                    }
                  />
                  {modalErrors.customer_email && (
                    <small className="text-danger d-block mt-1">
                      {modalErrors.customer_email}
                    </small>
                  )}
                </div>

                {/* Protocol Number - Only show if required */}
                {isProtocolNumberRequired(modalData.country_name) && (
                  <div>
                    <Label className="fw-bold mb-2">
                      Protocol Number
                      <span style={{ color: "red", marginLeft: "4px" }}>*</span>
                    </Label>
                    <Input
                      bsSize="sm"
                      type="text"
                      value={modalData.protocol_number || ""}
                      onChange={(e) =>
                        handleModalFieldChange("protocol_number", e.target.value)
                      }
                      placeholder="Enter Protocol Number (Required for Italy, Slovenia, Croatia)"
                      disabled={isSubmitting}
                      style={{ backgroundColor: "#fff" }}
                      className={
                        modalErrors.protocol_number ? "is-invalid" : ""
                      }
                    />
                    {modalErrors.protocol_number && (
                      <small className="text-danger d-block mt-1">
                        {modalErrors.protocol_number}
                      </small>
                    )}
                  </div>
                )}

                {/* Effective Date */}
                <div>
                  <Label className="fw-bold mb-2">
                    Effective Date
                    <span style={{ color: "red", marginLeft: "4px" }}>*</span>
                  </Label>
                  <Input
                    bsSize="sm"
                    type="date"
                    value={
                      modalData.effective_date
                        ? moment(modalData.effective_date).format("YYYY-MM-DD")
                        : ""
                    }
                    onChange={(e) =>
                      handleModalFieldChange("effective_date", e.target.value)
                    }
                    disabled={isSubmitting}
                    style={{ backgroundColor: "#fff" }}
                    className={
                      modalErrors.effective_date ? "is-invalid" : ""
                    }
                  />
                  {modalErrors.effective_date && (
                    <small className="text-danger d-block mt-1">
                      {modalErrors.effective_date}
                    </small>
                  )}
                </div>
                {/* Validity Period */}
                <div>
                  <Label className="fw-bold mb-2">
                    Validity Period
                    <span style={{ color: "red", marginLeft: "4px" }}>*</span>
                  </Label>
                  <select
                    className={`form-select form-select-sm ${modalErrors.validity_period ? "is-invalid" : ""
                      }`}
                    value={modalData.validity_period || ""}
                    onChange={(e) =>
                      handleModalFieldChange("validity_period", e.target.value)
                    }
                    style={{ padding: "6px 8px", backgroundColor: "#fff" }}
                    disabled={isSubmitting}
                  >
                    <option value="">Select Validity Period</option>
                    {validityPeriodOptions.map((option) => (
                      <option key={option} value={option}>
                        {option}
                      </option>
                    ))}
                  </select>
                  {modalErrors.validity_period && (
                    <small className="text-danger d-block mt-1">
                      {modalErrors.validity_period}
                    </small>
                  )}
                </div>
                {/* Expiry Date */}
                <div>
                  <Label className="fw-bold mb-2">
                    Expiry Date
                    <span style={{ color: "red", marginLeft: "4px" }}>*</span>
                  </Label>
                  <Input
                    bsSize="sm"
                    type="date"
                    min={getTodayDate()}
                    value={
                      modalData.expiry_date
                        ? moment(modalData.expiry_date).format("YYYY-MM-DD")
                        : ""
                    }
                    onChange={(e) =>
                      handleModalFieldChange("expiry_date", e.target.value)
                    }
                    disabled={isSubmitting}
                    style={{ backgroundColor: "#fff" }}
                    className={modalErrors.expiry_date ? "is-invalid" : ""}
                  />
                  {modalErrors.expiry_date && (
                    <small className="text-danger d-block mt-1">
                      {modalErrors.expiry_date}
                    </small>
                  )}
                </div>

                {/* Upload Document */}
                <div style={{ gridColumn: "1 / -1" }}>
                  <Label className="fw-bold mb-2">
                    Upload Document
                    <span style={{ color: "red", marginLeft: "4px" }}>*</span>
                  </Label>
                  <Input
                    bsSize="sm"
                    type="file"
                    onChange={handleFileUpload}
                    accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                    style={{ backgroundColor: "#fff" }}
                    className={modalErrors.file ? "is-invalid" : ""}
                    disabled={isSubmitting}
                  />
                  {uploadedFile && (
                    <small className="text-success d-block mt-2">
                      ✓ Selected: {uploadedFile.name}
                    </small>
                  )}
                  {modalErrors.file && (
                    <small className="text-danger d-block mt-2">
                      {modalErrors.file}
                    </small>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
        <div className="modal-footer">
          <Button color="secondary" onClick={closeModal} disabled={isSubmitting}>
            Cancel
          </Button>
          <Button color="primary" onClick={updateRow} disabled={isSubmitting}>
            {isSubmitting ? (
              <>
                <span
                  className="spinner-border spinner-border-sm me-2"
                  role="status"
                  aria-hidden="true"
                ></span>
                Creating...
              </>
            ) : (
              "Create"
            )}
          </Button>
        </div>
      </Modal>

      {/* Edit/Duplicate Modal */}
      <Modal
        isOpen={modalOpen && !isCreateMode}
        toggle={closeModal}
        centered
        size="lg"
      >
        <div className="modal-header border-bottom">
          <h5 className="mb-0">
            {modalData?.isDuplicate
              ? "Create Tax Certificate (Duplicate)"
              : "Edit Tax Certificate"}
          </h5>
          <button
            type="button"
            className="btn-close"
            onClick={closeModal}
            disabled={isSubmitting}
          ></button>
        </div>
        <div
          className="modal-body bg-light"
          style={{ maxHeight: "70vh", overflowY: "auto", padding: "20px" }}
        >
          {modalData && (
            <div>
              {modalErrors.submit && (
                <div
                  className="alert alert-danger alert-dismissible fade show"
                  role="alert"
                >
                  {modalErrors.submit}
                  <button
                    type="button"
                    className="btn-close"
                    onClick={() =>
                      setModalErrors({ ...modalErrors, submit: "" })
                    }
                    disabled={isSubmitting}
                  />
                </div>
              )}

              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "1fr 1fr",
                  gap: "16px",
                }}
              >
                {/* Order Number - Read-only */}
                <div>
                  <Label className="fw-bold mb-2">Order Number</Label>
                  <Input
                    bsSize="sm"
                    type="text"
                    value={modalData.order_number}
                    onChange={(e) => null}
                    disabled={true}
                    style={{ backgroundColor: "#e9ecef", cursor: "not-allowed" }}
                    placeholder=""
                  />
                </div>

                {/* Customer Sold-To-ID */}
                <div>
                  <Label className="fw-bold mb-2">
                    Customer Sold-To-ID
                    {modalData.isDuplicate && (
                      <span style={{ color: "red", marginLeft: "4px" }}>*</span>
                    )}
                  </Label>
                  <Input
                    bsSize="sm"
                    type="text"
                    value={modalData.cust_code}
                    onChange={(e) =>
                      modalData.isDuplicate
                        ? handleModalFieldChange("cust_code", e.target.value)
                        : null
                    }
                    disabled={!modalData.isDuplicate || isSubmitting}
                    style={
                      modalData.isDuplicate
                        ? { backgroundColor: "#fff" }
                        : { backgroundColor: "#e9ecef", cursor: "not-allowed" }
                    }
                    placeholder={
                      modalData.isDuplicate
                        ? "Enter customer code"
                        : ""
                    }
                    className={
                      modalData.isDuplicate && modalErrors.cust_code
                        ? "is-invalid"
                        : ""
                    }
                  />
                  {modalData.isDuplicate && modalErrors.cust_code && (
                    <small className="text-danger d-block mt-1">
                      {modalErrors.cust_code}
                    </small>
                  )}
                </div>

                {/* Customer Name */}
                <div>
                  <Label className="fw-bold mb-2">
                    Customer Name
                    {modalData.isDuplicate && (
                      <span style={{ color: "red", marginLeft: "4px" }}>*</span>
                    )}
                  </Label>
                  <Input
                    bsSize="sm"
                    type="text"
                    value={modalData.cust_name}
                    onChange={(e) =>
                      modalData.isDuplicate
                        ? handleModalFieldChange("cust_name", e.target.value)
                        : null
                    }
                    disabled={!modalData.isDuplicate || isSubmitting}
                    style={
                      modalData.isDuplicate
                        ? { backgroundColor: "#fff" }
                        : { backgroundColor: "#e9ecef", cursor: "not-allowed" }
                    }
                    placeholder={
                      modalData.isDuplicate
                        ? "Enter customer name"
                        : ""
                    }
                    className={
                      modalData.isDuplicate && modalErrors.cust_name
                        ? "is-invalid"
                        : ""
                    }
                  />
                  {modalData.isDuplicate && modalErrors.cust_name && (
                    <small className="text-danger d-block mt-1">
                      {modalErrors.cust_name}
                    </small>
                  )}
                </div>

                {/* Vessel IMO */}
                <div>
                  <Label className="fw-bold mb-2">
                    Vessel IMO
                    {modalData.isDuplicate && (
                      <span style={{ color: "red", marginLeft: "4px" }}>*</span>
                    )}
                  </Label>
                  <Input
                    bsSize="sm"
                    type="text"
                    value={modalData.vessel_imo_number}
                    onChange={(e) =>
                      handleModalFieldChange("vessel_imo_number", e.target.value)
                    }
                    disabled={isSubmitting}
                    placeholder="Enter vessel IMO"
                    style={{ backgroundColor: "#fff" }}
                    className={
                      modalErrors.vessel_imo_number ? "is-invalid" : ""
                    }
                  />
                  {modalErrors.vessel_imo_number && (
                    <small className="text-danger d-block mt-1">
                      {modalErrors.vessel_imo_number}
                    </small>
                  )}
                </div>

                {/* Vessel Name */}
                <div>
                  <Label className="fw-bold mb-2">
                    Vessel Name
                    {modalData.isDuplicate && (
                      <span style={{ color: "red", marginLeft: "4px" }}>*</span>
                    )}
                  </Label>
                  <Input
                    bsSize="sm"
                    type="text"
                    value={modalData.vessel_name}
                    onChange={(e) =>
                      handleModalFieldChange("vessel_name", e.target.value)
                    }
                    disabled={isSubmitting}
                    placeholder="Enter vessel name"
                    style={{ backgroundColor: "#fff" }}
                    className={
                      modalErrors.vessel_name ? "is-invalid" : ""
                    }
                  />
                  {modalErrors.vessel_name && (
                    <small className="text-danger d-block mt-1">
                      {modalErrors.vessel_name}
                    </small>
                  )}
                </div>

                {/* Document Type */}
                <div>
                  <Label className="fw-bold mb-2">
                    Document Type
                    {modalData.isDuplicate && (
                      <span style={{ color: "red", marginLeft: "4px" }}>*</span>
                    )}
                  </Label>
                  {modalData.isDuplicate ? (
                    <select
                      className={`form-select form-select-sm ${modalErrors.tax_type ? "is-invalid" : ""
                        }`}
                      value={modalData.tax_type || ""}
                      onChange={(e) =>
                        handleModalFieldChange("tax_type", e.target.value)
                      }
                      style={{ padding: "6px 8px", backgroundColor: "#fff" }}
                      disabled={isSubmitting}
                    >
                      <option value="">Select Document Type</option>
                      {documentTypeOptions.map((option) => (
                        <option key={option} value={option}>
                          {option}
                        </option>
                      ))}
                    </select>
                  ) : (
                    <Input
                      bsSize="sm"
                      type="text"
                      value={modalData.tax_type || ""}
                      onChange={() => null}
                      disabled={true}
                      style={{
                        backgroundColor: "#e9ecef",
                        cursor: "not-allowed",
                      }}
                      placeholder=""
                    />
                  )}
                  {modalData.isDuplicate && modalErrors.tax_type && (
                    <small className="text-danger d-block mt-1">
                      {modalErrors.tax_type}
                    </small>
                  )}
                </div>

                {/* Document ID - Only show in Edit mode */}
                {!modalData.isDuplicate && (
                  <div>
                    <Label className="fw-bold mb-2">Document ID</Label>
                    <Input
                      bsSize="sm"
                      type="text"
                      value={modalData.document_id}
                      onChange={(e) => null}
                      disabled={true}
                      style={{
                        backgroundColor: "#e9ecef",
                        cursor: "not-allowed",
                      }}
                      placeholder=""
                    />
                  </div>
                )}

                {/* Tax Document ID */}
                <div>
                  <Label className="fw-bold mb-2">
                    Tax Document ID
                    {modalData.isDuplicate && (
                      <span style={{ color: "red", marginLeft: "4px" }}>*</span>
                    )}
                  </Label>
                  <Input
                    bsSize="sm"
                    type="text"
                    value={modalData.tax_document_id}
                    onChange={(e) =>
                      modalData.isDuplicate
                        ? handleModalFieldChange("tax_document_id", e.target.value)
                        : null
                    }
                    disabled={!modalData.isDuplicate || isSubmitting}
                    style={
                      modalData.isDuplicate
                        ? { backgroundColor: "#fff" }
                        : { backgroundColor: "#e9ecef", cursor: "not-allowed" }
                    }
                    placeholder={
                      modalData.isDuplicate
                        ? "Enter Tax Document Id"
                        : ""
                    }
                    className={
                      modalData.isDuplicate && modalErrors.tax_document_id
                        ? "is-invalid"
                        : ""
                    }
                  />
                  {modalData.isDuplicate && modalErrors.tax_document_id && (
                    <small className="text-danger d-block mt-1">
                      {modalErrors.tax_document_id}
                    </small>
                  )}
                </div>

                {/* Tax Name */}
                <div>
                  <Label className="fw-bold mb-2">
                    Tax Name
                    {modalData.isDuplicate && (
                      <span style={{ color: "red", marginLeft: "4px" }}>*</span>
                    )}
                  </Label>
                  <Input
                    bsSize="sm"
                    type="text"
                    value={modalData.tax_name || ""}
                    onChange={(e) =>
                      modalData.isDuplicate
                        ? handleModalFieldChange("tax_name", e.target.value)
                        : null
                    }
                    disabled={!modalData.isDuplicate || isSubmitting}
                    style={
                      modalData.isDuplicate
                        ? { backgroundColor: "#fff" }
                        : { backgroundColor: "#e9ecef", cursor: "not-allowed" }
                    }
                    placeholder={
                      modalData.isDuplicate
                        ? "Enter tax name"
                        : ""
                    }
                    className={
                      modalData.isDuplicate && modalErrors.tax_name
                        ? "is-invalid"
                        : ""
                    }
                  />
                  {modalData.isDuplicate && modalErrors.tax_name && (
                    <small className="text-danger d-block mt-1">
                      {modalErrors.tax_name}
                    </small>
                  )}
                </div>

                {/* Customer Email - Only show in Duplicate mode */}
                {modalData?.isDuplicate && (
                  <div>
                    <Label className="fw-bold mb-2">
                      Customer Email
                      <span style={{ color: "red", marginLeft: "4px" }}>*</span>
                    </Label>
                    <Input
                      bsSize="sm"
                      type="text"
                      value={modalData.customer_email || ""}
                      onChange={(e) =>
                        handleModalFieldChange(
                          "customer_email",
                          e.target.value
                        )
                      }
                      disabled={!modalData.isDuplicate || isSubmitting}
                      style={{ backgroundColor: "#fff" }}
                      placeholder="Enter customer email"
                      className={
                        modalErrors.customer_email
                          ? "is-invalid"
                          : ""
                      }
                    />
                    {modalErrors.customer_email && (
                      <small className="text-danger d-block mt-1">
                        {modalErrors.customer_email}
                      </small>
                    )}
                  </div>
                )}

                {/* Region Dropdown */}
                {renderRegionDropdown()}

                {/* Country Dropdown */}
                {renderCountryDropdown()}

                {/* Port Dropdown */}
                {renderPortDropdown()}

                {/* State ISO Code - only shown when country is UNITED STATES */}
                {modalData?.country_name === 'UNITED STATES' && (
                  <div>
                    <SearchableDropdown
                      label="State ISO Code"
                      fieldKey="state_iso_code"
                      options={stateIsoCodeOptions}
                      selectedValue={
                        modalData?.state_iso_code
                          ? (() => {
                            const rawStateData = filterOptions['state_iso_code'] || [];
                            const stateObj = rawStateData.find(
                              (state: any) => (state?.isocode ?? String(state)) === modalData.state_iso_code
                            );
                            return stateObj?.name || modalData.state_iso_code;
                          })()
                          : ""
                      }
                      searchValue={searchText.state_iso_code}
                      placeholder="Select State ISO Code"
                      isRequired={false}
                      openDropdown={openDropdown}
                      disabled={!modalData?.isDuplicate}
                      onToggle={handleDropdownToggle}
                      onSearchChange={handleSearchChange}
                      onSelect={(value) => {
                        isSelectingRef.current = true;
                        handleDropdownFieldChange('state_iso_code', value);
                        setOpenDropdown(null);
                      }}
                    />
                    {modalErrors.state_iso_code && (
                      <small className="text-danger d-block mt-1">
                        {modalErrors.state_iso_code}
                      </small>
                    )}
                  </div>
                )}

                {/* Account Manager */}
                {/* <div>
                  <Label className="fw-bold mb-2">
                    Account Manager
                    {modalData.isDuplicate && (
                      <span style={{ color: "red", marginLeft: "4px" }}>*</span>
                    )}
                  </Label>
                  <Input
                    bsSize="sm"
                    type="text"
                    value={modalData.account_manager || ""}
                    onChange={(e) =>
                      modalData.isDuplicate
                        ? handleModalFieldChange(
                          "account_manager",
                          e.target.value
                        )
                        : null
                    }
                    disabled={!modalData.isDuplicate || isSubmitting}
                    style={
                      modalData.isDuplicate
                        ? { backgroundColor: "#fff" }
                        : { backgroundColor: "#e9ecef", cursor: "not-allowed" }
                    }
                    placeholder={
                      modalData.isDuplicate
                        ? "Enter account manager"
                        : ""
                    }
                    className={
                      modalData.isDuplicate && modalErrors.account_manager
                        ? "is-invalid"
                        : ""
                    }
                  />
                  {modalData.isDuplicate && modalErrors.account_manager && (
                    <small className="text-danger d-block mt-1">
                      {modalErrors.account_manager}
                    </small>
                  )}
                </div> */}

                {/* Sales Country */}
                {/* <div>
                  <Label className="fw-bold mb-2">
                    Sales Country
                    {modalData.isDuplicate && (
                      <span style={{ color: "red", marginLeft: "4px" }}>*</span>
                    )}
                  </Label>
                  <Input
                    bsSize="sm"
                    type="text"
                    value={modalData.sales_country || ""}
                    onChange={(e) =>
                      modalData.isDuplicate
                        ? handleModalFieldChange("sales_country", e.target.value)
                        : null
                    }
                    disabled={!modalData.isDuplicate || isSubmitting}
                    style={
                      modalData.isDuplicate
                        ? { backgroundColor: "#fff" }
                        : { backgroundColor: "#e9ecef", cursor: "not-allowed" }
                    }
                    placeholder={
                      modalData.isDuplicate
                        ? "Enter sales country"
                        : ""
                    }
                    className={
                      modalData.isDuplicate && modalErrors.sales_country
                        ? "is-invalid"
                        : ""
                    }
                  />
                  {modalData.isDuplicate && modalErrors.sales_country && (
                    <small className="text-danger d-block mt-1">
                      {modalErrors.sales_country}
                    </small>
                  )}
                </div> */}

                {/* Protocol Number - Only show if required for country */}
                {isProtocolNumberRequired(modalData.country_name) && (
                  <div>
                    <Label className="fw-bold mb-2">
                      Protocol Number
                      <span style={{ color: "red", marginLeft: "4px" }}>*</span>
                    </Label>
                    <Input
                      bsSize="sm"
                      type="text"
                      value={modalData.protocol_number || ""}
                      onChange={(e) =>
                        handleModalFieldChange("protocol_number", e.target.value)
                      }
                      placeholder="Enter Protocol Number (Required for Italy, Slovenia, Croatia)"
                      disabled={isSubmitting}
                      style={{ backgroundColor: "#fff" }}
                      className={
                        modalErrors.protocol_number ? "is-invalid" : ""
                      }
                    />
                    {modalErrors.protocol_number && (
                      <small className="text-danger d-block mt-1">
                        {modalErrors.protocol_number}
                      </small>
                    )}
                  </div>
                )}

                {/* Status */}
                <div>
                  <Label className="fw-bold mb-2">Status</Label>
                  <select
                    className="form-select form-select-sm"
                    value={modalData.status}
                    onChange={(e) =>
                      handleModalFieldChange("status", e.target.value)
                    }
                    style={{ padding: "6px 8px", backgroundColor: "#fff" }}
                    disabled={isSubmitting}
                  >
                    {statusOptions.map((option) => (
                      <option key={option} value={option}>
                        {option}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Invalid Reason - Show only if Status is Invalid */}
                {modalData.status === "Invalid" && (
                  <div>
                    <Label className="fw-bold mb-2">
                      Invalid Reason
                      <span style={{ color: "red", marginLeft: "4px" }}>*</span>
                    </Label>
                    <Input
                      bsSize="sm"
                      type="text"
                      value={modalData.invalid_reason || ""}
                      onChange={(e) =>
                        handleModalFieldChange(
                          "invalid_reason",
                          e.target.value
                        )
                      }
                      placeholder="Enter reason for invalid status"
                      disabled={isSubmitting}
                      style={{ backgroundColor: "#fff" }}
                      className={
                        modalErrors.invalid_reason ? "is-invalid" : ""
                      }
                    />
                    {modalErrors.invalid_reason && (
                      <small className="text-danger d-block mt-1">
                        {modalErrors.invalid_reason}
                      </small>
                    )}
                  </div>
                )}

                {/* Effective Date */}
                <div>
                  <Label className="fw-bold mb-2">Effective Date</Label>
                  <Input
                    bsSize="sm"
                    type="date"
                    value={
                      modalData.effective_date
                        ? moment(modalData.effective_date).format("YYYY-MM-DD")
                        : ""
                    }
                    onChange={(e) =>
                      handleModalFieldChange("effective_date", e.target.value)
                    }
                    disabled={isSubmitting}
                    style={{ backgroundColor: "#fff" }}
                  />
                </div>
                {/* Validity Period */}
                <div>
                  <Label className="fw-bold mb-2">
                    Validity Period
                    <span style={{ color: "red", marginLeft: "4px" }}>*</span>
                  </Label>
                  <select
                    className={`form-select form-select-sm ${modalErrors.validity_period ? "is-invalid" : ""
                      }`}
                    value={
                      modalData.validity_period
                        ? modalData.validity_period.trim()
                        : ""
                    }
                    onChange={(e) =>
                      handleModalFieldChange("validity_period", e.target.value)
                    }
                    style={{ padding: "6px 8px", backgroundColor: "#fff" }}
                    disabled={isSubmitting}
                  >
                     <option value="">{modalData.validity_period}</option>
                    {validityPeriodOptions.map((option) => (
                      <option key={option} value={option}>
                        {option}
                      </option>
                    ))}
                  </select>
                  {modalErrors.validity_period && (
                    <small className="text-danger d-block mt-1">
                      {modalErrors.validity_period}
                    </small>
                  )}
                </div>

                {/* Expiry Date */}
                <div>
                  <Label className="fw-bold mb-2">Expiry Date</Label>
                  <Input
                    bsSize="sm"
                    type="date"
                    min={getTodayDate()}
                    value={
                      modalData.expiry_date
                        ? moment(modalData.expiry_date).format("YYYY-MM-DD")
                        : ""
                    }
                    onChange={(e) =>
                      handleModalFieldChange("expiry_date", e.target.value)
                    }
                    disabled={isSubmitting}
                    style={{ backgroundColor: "#fff" }}
                  />
                </div>

                {/* Upload Document */}
                <div style={{ gridColumn: "1 / -1" }}>
                  <Label className="fw-bold mb-2">Upload Document</Label>
                  <Input
                    bsSize="sm"
                    type="file"
                    onChange={handleFileUpload}
                    accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                    style={{ backgroundColor: "#fff" }}
                    className={modalErrors.file ? "is-invalid" : ""}
                    disabled={isSubmitting}
                  />
                  {uploadedFile && (
                    <small className="text-success d-block mt-2">
                      ✓ Selected: {uploadedFile.name}
                    </small>
                  )}
                  {modalErrors.file && (
                    <small className="text-danger d-block mt-2">
                      {modalErrors.file}
                    </small>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
        <div className="modal-footer">
          <Button color="secondary" onClick={closeModal} disabled={isSubmitting}>
            Cancel
          </Button>
          <Button color="primary" onClick={updateRow} disabled={isSubmitting}>
            {isSubmitting ? (
              <>
                <span
                  className="spinner-border spinner-border-sm me-2"
                  role="status"
                  aria-hidden="true"
                ></span>
                {modalData?.isDuplicate ? "Creating..." : "Updating..."}
              </>
            ) : (
              modalData?.isDuplicate ? "Create" : "Update"
            )}
          </Button>
        </div>
      </Modal>
    </>
  );
}